import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import SeancesEditor from "../../components/lab/SeancesEditor";
import ConflitSalle from "../../components/lab/ConflitSalle";
import "../../styles/lab/lab.css";
import {
  getSessions,
  ajouterSession,
  patchSession,
} from "../../services/infoCentre/session_formation_service";
import { getSalles, getSallesLibresPourSeances } from "../../services/lab/salleService";
import { getFormateurs } from "../../services/infoCentre/Formateurservice";
import { getFormations } from "../../services/infoCentre/formationService";
import authService from "../../services/auth/authService";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
              "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin",
                    "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const JOURS_COURTS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const JOURS_LONGS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const COULEUR_STATUT = {
  planifiee: "#22c55e",
  en_cours:  "#3b82f6",
  terminee:  "#94a3b8",
  reportee:  "#f59e0b",
  annulee:   "#ef4444",
};

// Une couleur par session (point 5 de l'équipe) : la même session garde la
// même couleur d'un jour à l'autre. Le statut reste visible en pastille.
const PALETTE_SESSIONS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6",
  "#14b8a6", "#f97316", "#ef4444", "#84cc16", "#06b6d4", "#a855f7",
];
const couleurSession = (s) => PALETTE_SESSIONS[(Number(s.id) || 0) % PALETTE_SESSIONS.length];
const styleSession = (s) => {
  const c = couleurSession(s);
  return { borderLeftColor: c, background: `${c}1f` };   // fond teinté à ~12 %
};
const nomsFormateurs = (s) => (s.formateurs_detail || []).map((f) => f.nom_complet).join(", ");

const STATUTS = [
  { value: "planifiee", label: "Planifiée" },
  { value: "en_cours",  label: "En cours" },
  { value: "reportee",  label: "Reportée" },
  { value: "annulee",   label: "Annulée" },
  { value: "terminee",  label: "Terminée" },
];
const MODES = [
  { value: "presentiel", label: "Présentiel" },
  { value: "distanciel", label: "Distanciel" },
  { value: "hybride",    label: "Hybride" },
];
const NIVEAUX = [
  { value: "",              label: "Non défini" },
  { value: "debutant",      label: "Débutant" },
  { value: "intermediaire", label: "Intermédiaire" },
  { value: "avance",        label: "Avancé" },
];

const VUES = [
  { value: "mois",       label: "Mois",       icone: "fa-calendar" },
  { value: "semaine",    label: "Semaine",    icone: "fa-calendar-week" },
  { value: "jour",       label: "Jour",       icone: "fa-calendar-day" },
  { value: "intervalle", label: "Intervalle", icone: "fa-calendar-days" },
];

// Grille horaire de la vue Semaine : lignes 8h-9h, 9h-10h, … 16h-17h.
const HEURE_DEBUT_GRILLE = 8;
const HEURE_FIN_GRILLE   = 17;
const HEURES_GRILLE = Array.from(
  { length: HEURE_FIN_GRILLE - HEURE_DEBUT_GRILLE },
  (_, i) => HEURE_DEBUT_GRILLE + i
);
const HAUTEUR_LIGNE = 56; // px par heure

const FORM_VIDE = {
  formation: "", intitule_session: "", formateurs: [],
  salle: "", date_debut: "", date_fin: "", seances: [],
  mode: "presentiel", niveau: "", statut: "planifiee",
};

// Champs de la modale qui ont leur propre message d'erreur inline ; le reste
// (detail, non_field_errors, dates, duree…) est regroupé en bannière.
const CHAMPS_CONNUS = ["formation", "intitule_session", "date_debut", "date_fin",
                       "salle", "formateurs", "mode", "niveau", "statut", "seances"];

const pad2 = (n) => String(n).padStart(2, "0");
const jour = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const indexJour = (d) => (d.getDay() + 6) % 7; // lundi = 0

const debutSemaine = (d) => {
  const r = jour(d);
  r.setDate(r.getDate() - indexJour(r));
  return r;
};
const finSemaine = (d) => {
  const r = debutSemaine(d);
  r.setDate(r.getDate() + 6);
  return r;
};

const estMinuit = (d) => d.getHours() === 0 && d.getMinutes() === 0;

/** Libellé horaire d'une session : « Journée » si aucune heure n'est renseignée. */
const creneauHoraire = (s) => {
  const d1 = new Date(s.date_debut);
  const d2 = new Date(s.date_fin);
  if (estMinuit(d1) && estMinuit(d2)) return "Journée";
  return `${pad2(d1.getHours())}:${pad2(d1.getMinutes())} → ${pad2(d2.getHours())}:${pad2(d2.getMinutes())}`;
};

const iso10 = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const triHoraire = (a, b) => new Date(a.date_debut) - new Date(b.date_debut);

/**
 * Position d'une session sur la grille horaire (8h-17h) d'un jour donné :
 * { depart, duree } en heures relatives à HEURE_DEBUT_GRILLE, ou null si la
 * session est "Journée" (aucune heure) ou entièrement hors de la plage —
 * elle s'affiche alors dans la bande "Journée" au lieu de disparaître.
 */
const plageGrille = (s) => {
  const d1 = new Date(s.date_debut);
  const d2 = new Date(s.date_fin);
  if (estMinuit(d1) && estMinuit(d2)) return null;
  const h1 = d1.getHours() + d1.getMinutes() / 60;
  const h2 = d2.getHours() + d2.getMinutes() / 60;
  if (h2 <= h1) return null;
  const depart = Math.max(h1, HEURE_DEBUT_GRILLE);
  const fin = Math.min(h2, HEURE_FIN_GRILLE);
  if (depart >= fin) return null;
  return { depart: depart - HEURE_DEBUT_GRILLE, duree: fin - depart };
};

/**
 * Assigne à chaque session positionnée une colonne (et le nombre total de
 * colonnes concurrentes ce jour-là) pour afficher les chevauchements côte à
 * côte plutôt que superposés — chacune reste ainsi visible et cliquable.
 */
const disposerChevauchements = (items) => {
  const tries = [...items].sort((a, b) => a.plage.depart - b.plage.depart);
  const finColonnes = [];
  const poses = tries.map((it) => {
    const debut = it.plage.depart;
    const fin = it.plage.depart + it.plage.duree;
    let colonne = finColonnes.findIndex((f) => f <= debut);
    if (colonne === -1) { colonne = finColonnes.length; finColonnes.push(fin); }
    else finColonnes[colonne] = fin;
    return { ...it, colonne };
  });
  const total = finColonnes.length || 1;
  return poses.map((p) => ({ ...p, total }));
};

/** Aplati les erreurs DRF ({champ: [msg]} ou string) en {champ: "msg"}. */
const erreursBackend = (err) => {
  const data = err?.response?.data;
  if (!data) return { detail: "Impossible d'enregistrer. Vérifiez la connexion." };
  if (typeof data === "string") return { detail: data };
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    out[k] = Array.isArray(v) ? v.join(" ") : String(v);
  }
  return out;
};

export default function Planning() {
  const peutGerer = authService.peutPlanifier();

  const [sessions, setSessions]   = useState([]);
  const [salles, setSalles]       = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [formations, setFormations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [flash, setFlash]         = useState(null);

  // Sur téléphone, la grille du mois est illisible : on ouvre la semaine en liste.
  const [vue, setVue]             = useState(() =>
    window.matchMedia?.("(max-width: 900px)").matches ? "intervalle" : "mois");
  const [curseur, setCurseur]     = useState(new Date());
  const [intervalleDebut, setIntervalleDebut] = useState(() => iso10(debutSemaine(new Date())));
  const [intervalleFin, setIntervalleFin]     = useState(() => iso10(finSemaine(new Date())));

  const [filtreSalle, setFiltreSalle]         = useState("");
  const [filtreStatut, setFiltreStatut]       = useState("");
  const [filtreFormateur, setFiltreFormateur] = useState("");
  const [filtreFormation, setFiltreFormation] = useState("");
  const [recherche, setRecherche]             = useState("");

  const [modale, setModale]       = useState(null);   // null | "creer" | "modifier"
  const [ouverture, setOuverture] = useState(0);      // remonte l'éditeur de séances à chaque ouverture
  const [courante, setCourante]   = useState(null);
  const [form, setForm]           = useState(FORM_VIDE);
  const [erreursForm, setErreursForm] = useState({});
  const [enregistrement, setEnregistrement] = useState(false);
  const [sallesLibres, setSallesLibres] = useState(null); // null = non vérifié ; sinon Set d'ids libres
  const [verifSalles, setVerifSalles]   = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      // allSettled et non all : une liste refusée par le rôle (ex. les salles)
      // ne doit pas vider tout le planning, comme c'était le cas auparavant.
      const [s, sl, fo, fm] = (await Promise.allSettled([
        getSessions(),
        getSalles(),
        getFormateurs(),
        getFormations(),
      ])).map((r) => (r.status === "fulfilled" ? r.value : []));
      setSessions(Array.isArray(s) ? s : (s?.results ?? []));
      setSalles(Array.isArray(sl) ? sl : (sl?.results ?? []));
      setFormateurs(Array.isArray(fo) ? fo : (fo?.results ?? []));
      // getFormations() renvoie la réponse axios brute (pas .data).
      const fmData = fm?.data ?? fm;
      setFormations(Array.isArray(fmData) ? fmData : (fmData?.results ?? []));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  // Salles libres pour TOUTES les séances saisies (aide visuelle). Le conflit
  // est de toute façon refusé par le serveur à l'enregistrement.
  const seancesValides = useMemo(() => (form.seances || []).filter(
    (s) => s.date && s.heure_debut && s.heure_fin && s.heure_debut < s.heure_fin
  ), [form.seances]);
  useEffect(() => {
    if (!modale || form.mode === "distanciel" || !seancesValides.length) {
      setSallesLibres(null); setVerifSalles(false); return undefined;
    }
    let annule = false;
    setVerifSalles(true);
    const t = setTimeout(async () => {
      try {
        const libres = await getSallesLibresPourSeances(seancesValides, courante?.id ?? null);
        if (!annule) setSallesLibres(new Set((libres || []).map((s) => String(s.id))));
      } catch {
        if (!annule) setSallesLibres(null);
      } finally {
        if (!annule) setVerifSalles(false);
      }
    }, 400);
    return () => { annule = true; clearTimeout(t); };
  }, [modale, form.mode, seancesValides, courante]);

  const filtresActifs = filtreSalle || filtreStatut || filtreFormateur || filtreFormation || recherche;
  const reinitialiser = () => {
    setFiltreSalle(""); setFiltreStatut(""); setFiltreFormateur("");
    setFiltreFormation(""); setRecherche("");
  };

  const filtrees = useMemo(() => sessions.filter((s) => {
    if (!s.date_debut || !s.date_fin) return false;
    if (filtreSalle && String(s.salle) !== filtreSalle) return false;
    if (filtreStatut && s.statut_session !== filtreStatut) return false;
    if (filtreFormation && String(s.formation) !== filtreFormation) return false;
    if (filtreFormateur && !(s.formateurs || []).map(String).includes(filtreFormateur)) return false;
    if (recherche.trim()) {
      const q = recherche.trim().toLowerCase();
      const foin = `${s.intitule_session || ""} ${s.formation_nom || ""}`.toLowerCase();
      if (!foin.includes(q)) return false;
    }
    return true;
  }), [sessions, filtreSalle, filtreStatut, filtreFormation, filtreFormateur, recherche]);

  /**
   * Une occurrence par séance, présentée comme une session d'un seul jour :
   * horaires, grille et chevauchements fonctionnent sans changement. Une
   * session sans séances garde l'ancien affichage, sur toute sa période.
   */
  const occurrences = useMemo(() => filtrees.flatMap((s) => (
    s.seances?.length
      ? s.seances.map((se) => ({
          ...s,
          session: s,
          cle: `${s.id}-${se.id ?? `${se.date}-${se.heure_debut}`}`,
          date_debut: `${se.date}T${se.heure_debut}`,
          date_fin: `${se.date}T${se.heure_fin}`,
        }))
      : [{ ...s, session: s, cle: String(s.id) }]
  )), [filtrees]);

  /** Occurrences d'un jour donné. */
  const sessionsDu = useCallback((d) => {
    const j = jour(d).getTime();
    return occurrences
      .filter((s) => {
        const deb = jour(new Date(s.date_debut)).getTime();
        const fin = jour(new Date(s.date_fin)).getTime();
        return j >= deb && j <= fin;
      })
      .sort(triHoraire);
  }, [occurrences]);

  // ── Grilles selon la vue ──
  const grilleMois = useMemo(() => {
    const annee = curseur.getFullYear();
    const mois  = curseur.getMonth();
    const decalage = indexJour(new Date(annee, mois, 1));
    const debut = new Date(annee, mois, 1 - decalage);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(debut);
      d.setDate(debut.getDate() + i);
      return d;
    });
  }, [curseur]);

  const joursSemaine = useMemo(() => {
    const debut = debutSemaine(curseur);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(debut);
      d.setDate(debut.getDate() + i);
      return d;
    });
  }, [curseur]);

  const joursIntervalle = useMemo(() => {
    const debut = new Date(intervalleDebut);
    const fin = new Date(intervalleFin);
    if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) return [];
    const jours = [];
    let d = jour(debut);
    const dernier = jour(fin).getTime();
    let garde = 0;
    while (d.getTime() <= dernier && garde < 400) {
      jours.push(new Date(d));
      d = new Date(d);
      d.setDate(d.getDate() + 1);
      garde += 1;
    }
    return jours;
  }, [intervalleDebut, intervalleFin]);

  const naviguer = (delta) => {
    const d = new Date(curseur);
    if (vue === "mois") d.setMonth(d.getMonth() + delta);
    else if (vue === "semaine") d.setDate(d.getDate() + 7 * delta);
    else d.setDate(d.getDate() + delta);
    setCurseur(d);
  };

  const libelleHeader = () => {
    if (vue === "mois") return `${MOIS[curseur.getMonth()]} ${curseur.getFullYear()}`;
    if (vue === "semaine") {
      const d1 = debutSemaine(curseur);
      const d2 = finSemaine(curseur);
      return `${d1.getDate()} ${MOIS_COURT[d1.getMonth()]} – ${d2.getDate()} ${MOIS_COURT[d2.getMonth()]} ${d2.getFullYear()}`;
    }
    if (vue === "jour") {
      return `${JOURS_LONGS[indexJour(curseur)]} ${curseur.getDate()} ${MOIS[curseur.getMonth()]} ${curseur.getFullYear()}`;
    }
    return "";
  };

  const moisCourant = curseur.getMonth();
  const aujourdhui = jour(new Date()).getTime();
  const sansDates = sessions.filter((s) => !s.date_debut || !s.date_fin);

  // ── Modale ──
  // Un clic sur une case crée une séance unique à ce jour (et cette heure en
  // vue Semaine) ; « Jours fixes » permet ensuite d'en faire une série.
  const ouvrirCreation = (dateBase, heureDebut) => {
    if (!peutGerer) return;
    const date = iso10(dateBase ? new Date(dateBase) : new Date());
    const h1 = Number.isFinite(heureDebut) ? heureDebut : 9;
    const h2 = Number.isFinite(heureDebut) ? heureDebut + 1 : 12;
    setForm({
      ...FORM_VIDE,
      date_debut: date,
      date_fin: date,
      seances: [{ date, heure_debut: `${pad2(h1)}:00`, heure_fin: `${pad2(h2)}:00` }],
    });
    setErreursForm({});
    setCourante(null);
    setOuverture((n) => n + 1);
    setModale("creer");
  };

  const ouvrirEdition = (occurrence) => {
    if (!peutGerer) return;
    const s = occurrence.session || occurrence;   // l'occurrence d'une séance → sa session
    setForm({
      formation: s.formation ? String(s.formation) : "",
      intitule_session: s.intitule_session || "",
      formateurs: (s.formateurs || []).map(String),
      salle: s.salle ? String(s.salle) : "",
      date_debut: s.date_debut ? iso10(new Date(s.date_debut)) : "",
      date_fin: s.date_fin ? iso10(new Date(s.date_fin)) : "",
      seances: (s.seances || []).map((se) => ({
        date: se.date, heure_debut: se.heure_debut.slice(0, 5), heure_fin: se.heure_fin.slice(0, 5),
      })),
      mode: s.mode || "presentiel",
      niveau: s.niveau || "",
      statut: s.statut_session || s.statut || "planifiee",
    });
    setErreursForm({});
    setCourante(s);
    setOuverture((n) => n + 1);
    setModale("modifier");
  };

  const fermerModale = () => { setModale(null); setEnregistrement(false); };

  // Salle déjà occupée (réponse « conflit_salle ») : on propose de changer de
  // salle ou de maintenir. Toute modification de la salle ou des séances rend
  // l'avertissement caduc.
  const [conflitSalle, setConflitSalle] = useState(null);
  useEffect(() => { setConflitSalle(null); }, [form.salle, form.mode, form.seances, ouverture]);

  const majForm = (champ, valeur) => setForm((f) => ({ ...f, [champ]: valeur }));

  // Stable (useCallback) : l'éditeur de séances l'utilise dans un effet.
  const changerSeances = useCallback((seances) => {
    setForm((f) => ({ ...f, seances }));
    setErreursForm((e) => (e.seances ? { ...e, seances: undefined } : e));
  }, []);

  const changerMode = (mode) =>
    setForm((f) => ({ ...f, mode, salle: mode === "distanciel" ? "" : f.salle }));

  const toggleFormateur = (id) => {
    const s = String(id);
    setForm((f) => ({
      ...f,
      formateurs: f.formateurs.includes(s)
        ? f.formateurs.filter((x) => x !== s)
        : [...f.formateurs, s],
    }));
  };

  const enregistrer = async (e, salle_partagee = false) => {
    e?.preventDefault();
    const errs = {};
    if (!form.formation) errs.formation = "Formation requise.";
    if (!form.intitule_session.trim()) errs.intitule_session = "Intitulé requis.";
    if (!form.seances.length) errs.seances = "Ajoutez au moins une séance.";
    else if (seancesValides.length !== form.seances.length)
      errs.seances = "Vérifiez les séances : chacune doit avoir une date et une heure de fin après l'heure de début.";
    if (Object.keys(errs).length) { setErreursForm(errs); return; }

    // La période (date_debut/date_fin) est déduite des séances par le serveur.
    const payload = {
      formation: Number(form.formation),
      intitule_session: form.intitule_session.trim(),
      formateurs: form.formateurs.map(Number),
      salle: (form.mode === "distanciel" || !form.salle) ? null : Number(form.salle),
      seances: form.seances,
      mode: form.mode,
      statut: form.statut,
      salle_partagee,
    };
    if (form.niveau) payload.niveau = form.niveau;

    setEnregistrement(true);
    try {
      if (modale === "creer") await ajouterSession(payload);
      else await patchSession(courante.id, payload);
      setModale(null);
      setFlash({ type: "succes", texte: modale === "creer" ? "Créneau créé." : "Créneau modifié." });
      await charger();
    } catch (err) {
      const conflit = err?.response?.data?.conflit_salle;
      if (conflit) setConflitSalle([].concat(conflit));
      else setErreursForm(erreursBackend(err));
    } finally {
      setEnregistrement(false);
    }
  };

  const banniereErreur = Object.entries(erreursForm)
    .filter(([k]) => !CHAMPS_CONNUS.includes(k))
    .map(([, v]) => v)
    .join(" ");

  // ── Rendus ──
  /** Infobulle complète d'une occurrence (nom, horaire, salle, formateurs, statut). */
  const infobulle = (s) => [
    s.intitule_session, s.formation_nom, creneauHoraire(s), s.salle_nom || s.mode_display,
    nomsFormateurs(s), s.statut_display,
  ].filter(Boolean).join("\n");

  const pastilleStatut = (s) => (
    <i className="planning-pastille-statut" title={s.statut_display}
       style={{ background: COULEUR_STATUT[s.statut_session] || "#94a3b8" }} />
  );

  const rendreEvenement = (s) => (
    <div
      key={s.cle}
      className={`planning-evenement ${peutGerer ? "cliquable" : ""}`}
      style={styleSession(s)}
      title={infobulle(s)}
      onClick={peutGerer ? (e) => { e.stopPropagation(); ouvrirEdition(s); } : undefined}
    >
      {pastilleStatut(s)}{s.intitule_session}
    </div>
  );

  const rendreLigneAgenda = (s) => (
    <div
      key={s.cle}
      className={`planning-agenda-ligne ${peutGerer ? "cliquable" : ""}`}
      style={styleSession(s)}
      onClick={peutGerer ? () => ouvrirEdition(s) : undefined}
    >
      <div className="planning-agenda-heure">{creneauHoraire(s)}</div>
      <div className="planning-agenda-corps">
        <strong>{s.intitule_session}</strong>
        <span>{s.formation_nom}</span>
      </div>
      <div className="planning-agenda-meta">
        {s.salle_nom && <span><i className="fa-solid fa-door-open"></i> {s.salle_nom}</span>}
        {!!s.formateurs_detail?.length && (
          <span><i className="fa-solid fa-chalkboard-user"></i> {nomsFormateurs(s)}</span>
        )}
        <span className="planning-etiquette" style={{ background: COULEUR_STATUT[s.statut_session] || "#94a3b8" }}>
          {s.statut_display}
        </span>
      </div>
    </div>
  );

  const rendreMois = () => (
    <div className="planning-grille">
      {JOURS_COURTS.map((j) => (
        <div key={j} className="planning-jour-entete">{j}</div>
      ))}
      {grilleMois.map((d, i) => {
        const duJour = sessionsDu(d);
        const horsMois = d.getMonth() !== moisCourant;
        const cEstAujourdhui = jour(d).getTime() === aujourdhui;
        return (
          <div
            key={i}
            className={`planning-case ${horsMois ? "hors-mois" : ""} ${cEstAujourdhui ? "aujourdhui" : ""} ${peutGerer ? "cliquable" : ""}`}
            onClick={peutGerer ? () => ouvrirCreation(d) : undefined}
          >
            <div className="planning-numero">{d.getDate()}</div>
            <div className="planning-evenements">
              {duJour.slice(0, 3).map(rendreEvenement)}
              {duJour.length > 3 && <div className="planning-plus">+{duJour.length - 3}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  const rendreSemaine = () => (
    <div className="planning-semaine-scroll">
      <div className="planning-semaine">
        <div className="planning-sem-axe-tete" />
        {joursSemaine.map((d, i) => {
          const cEstAujourdhui = jour(d).getTime() === aujourdhui;
          return (
            <div key={`entete-${i}`} className={`planning-sem-entete ${cEstAujourdhui ? "aujourdhui" : ""}`}>
              <span>{JOURS_COURTS[i]}</span>
              <strong>{d.getDate()}</strong>
            </div>
          );
        })}

        <div className="planning-sem-axe-journee">Journée</div>
        {joursSemaine.map((d, i) => {
          const journee = sessionsDu(d).filter((s) => !plageGrille(s));
          return (
            <div
              key={`journee-${i}`}
              className={`planning-sem-journee ${peutGerer ? "cliquable" : ""}`}
              onClick={peutGerer ? () => ouvrirCreation(d) : undefined}
            >
              {journee.map(rendreEvenement)}
            </div>
          );
        })}

        <div className="planning-sem-axe-heures" style={{ height: HEURES_GRILLE.length * HAUTEUR_LIGNE }}>
          {HEURES_GRILLE.map((h) => (
            <div key={h} className="planning-heure-etiquette" style={{ height: HAUTEUR_LIGNE }}>
              {pad2(h)}:00
            </div>
          ))}
        </div>
        {joursSemaine.map((d, i) => {
          const duJour = sessionsDu(d);
          const disposees = disposerChevauchements(
            duJour
              .map((s) => ({ session: s, plage: plageGrille(s) }))
              .filter((it) => it.plage)
          );
          const cEstAujourdhui = jour(d).getTime() === aujourdhui;
          return (
            <div
              key={`grille-${i}`}
              className={`planning-sem-grille ${cEstAujourdhui ? "aujourdhui" : ""}`}
              style={{ height: HEURES_GRILLE.length * HAUTEUR_LIGNE }}
            >
              {HEURES_GRILLE.map((h) => (
                <div
                  key={h}
                  className={`planning-heure-cellule ${peutGerer ? "cliquable" : ""}`}
                  style={{ height: HAUTEUR_LIGNE }}
                  onClick={peutGerer ? () => ouvrirCreation(d, h) : undefined}
                />
              ))}
              {disposees.map(({ session: s, plage, colonne, total }) => (
                <div
                  key={s.cle}
                  className={`planning-sem-bloc ${peutGerer ? "cliquable" : ""}`}
                  style={{
                    top: plage.depart * HAUTEUR_LIGNE,
                    height: Math.max(plage.duree * HAUTEUR_LIGNE - 2, 18),
                    left: `${(colonne / total) * 100}%`,
                    width: `calc(${100 / total}% - 2px)`,
                    ...styleSession(s),
                  }}
                  title={infobulle(s)}
                  onClick={peutGerer ? (e) => { e.stopPropagation(); ouvrirEdition(s); } : undefined}
                >
                  <strong>{pastilleStatut(s)}{s.intitule_session}</strong>
                  <span>{creneauHoraire(s)}</span>
                  {(s.salle_nom || s.mode === "distanciel") && (
                    <span><i className="fa-solid fa-door-open"></i> {s.salle_nom || "Distanciel"}</span>
                  )}
                  {!!s.formateurs_detail?.length && (
                    <span><i className="fa-solid fa-chalkboard-user"></i> {nomsFormateurs(s)}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );

  const rendreJour = () => {
    const duJour = sessionsDu(curseur);
    if (!duJour.length) {
      return <div className="planning-agenda-vide"><i className="fa-solid fa-calendar-day"></i><p>Aucune session ce jour-là.</p></div>;
    }
    return <div className="planning-agenda">{duJour.map(rendreLigneAgenda)}</div>;
  };

  const rendreIntervalle = () => {
    const groupes = joursIntervalle
      .map((d) => ({ d, sess: sessionsDu(d) }))
      .filter((g) => g.sess.length);
    if (!groupes.length) {
      return <div className="planning-agenda-vide"><i className="fa-solid fa-calendar-days"></i><p>Aucune session sur cet intervalle.</p></div>;
    }
    return (
      <div className="planning-agenda">
        {groupes.map(({ d, sess }) => (
          <div key={d.getTime()}>
            <div className="planning-agenda-jour">
              {JOURS_LONGS[indexJour(d)]} {d.getDate()} {MOIS_COURT[d.getMonth()]} {d.getFullYear()}
            </div>
            {sess.map(rendreLigneAgenda)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="lab-page">
        <header className="lab-header">
          <div>
            <h1><i className="fa-solid fa-calendar-days"></i> Planning</h1>
            <p className="lab-sous-titre">Les sessions du centre, à votre échelle</p>
          </div>
          <div className="lab-actions-tete">
            {peutGerer && (
              <button className="btn-primaire" onClick={() => ouvrirCreation(curseur)}>
                <i className="fa-solid fa-plus"></i> Nouveau créneau
              </button>
            )}
            <div className="planning-vues">
              {VUES.map((v) => (
                <button
                  key={v.value}
                  className={vue === v.value ? "actif" : ""}
                  onClick={() => setVue(v.value)}
                >
                  <i className={`fa-solid ${v.icone}`}></i> {v.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {flash && <div className={`lab-flash ${flash.type}`}>{flash.texte}</div>}

        <div className="planning-nav-barre">
          <button className="btn-lien" onClick={() => setCurseur(new Date())}>Aujourd'hui</button>
          {vue === "intervalle" ? (
            <div className="planning-intervalle-champs">
              <label>Du
                <input type="date" value={intervalleDebut}
                       onChange={(e) => setIntervalleDebut(e.target.value)} />
              </label>
              <label>Au
                <input type="date" value={intervalleFin}
                       onChange={(e) => setIntervalleFin(e.target.value)} />
              </label>
            </div>
          ) : (
            <div className="planning-nav">
              <button onClick={() => naviguer(-1)}><i className="fa-solid fa-chevron-left"></i></button>
              <strong>{libelleHeader()}</strong>
              <button onClick={() => naviguer(1)}><i className="fa-solid fa-chevron-right"></i></button>
            </div>
          )}
        </div>

        <div className="lab-barre">
          {/* Filtres sans objet masqués : formateurs et étudiants ne reçoivent ni
              les salles ni le catalogue, et un formateur ne voit que sa fiche. */}
          {salles.length > 0 && <label className="lab-champ">
            Salle
            <select value={filtreSalle} onChange={(e) => setFiltreSalle(e.target.value)}>
              <option value="">Toutes</option>
              {salles.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </label>}
          {formateurs.length > 1 && <label className="lab-champ">
            Formateur
            <select value={filtreFormateur} onChange={(e) => setFiltreFormateur(e.target.value)}>
              <option value="">Tous</option>
              {formateurs.map((f) => (
                <option key={f.id} value={f.id}>{f.nom_complet || `${f.prenom} ${f.nom}`}</option>
              ))}
            </select>
          </label>}
          {formations.length > 0 && <label className="lab-champ">
            Formation
            <select value={filtreFormation} onChange={(e) => setFiltreFormation(e.target.value)}>
              <option value="">Toutes</option>
              {formations.map((f) => (
                <option key={f.id} value={f.id}>{f.intitule}</option>
              ))}
            </select>
          </label>}
          <label className="lab-champ">
            Statut
            <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
              <option value="">Tous</option>
              {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <div className="lab-recherche">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              placeholder="Rechercher une session…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </div>
          {filtresActifs && (
            <button className="btn-lien" onClick={reinitialiser}>
              <i className="fa-solid fa-xmark"></i> Réinitialiser
            </button>
          )}
          <div className="planning-legende">
            <span className="planning-legende-titre">Une couleur par session · statut :</span>
            {Object.entries(COULEUR_STATUT).slice(0, 4).map(([k, c]) => (
              <span key={k}>
                <i style={{ background: c }}></i>
                {k === "planifiee" ? "Planifiée" : k === "en_cours" ? "En cours"
                  : k === "terminee" ? "Terminée" : "Reportée"}
              </span>
            ))}
          </div>
        </div>

        {chargement ? (
          <div className="lab-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>
        ) : (
          <>
            {vue === "mois" && rendreMois()}
            {vue === "semaine" && rendreSemaine()}
            {vue === "jour" && rendreJour()}
            {vue === "intervalle" && rendreIntervalle()}

            {!!sansDates.length && (
              <div className="planning-sans-dates">
                <strong>
                  <i className="fa-solid fa-circle-info"></i> {sansDates.length} session(s) sans dates
                </strong>
                <p>
                  Elles n'apparaissent pas au calendrier tant qu'elles ne sont pas
                  programmées.
                  {peutGerer && (
                    <> À compléter depuis{" "}
                      <Link to="/session-formations">Sessions de formation</Link>.</>
                  )}
                </p>
                <ul>
                  {sansDates.slice(0, 5).map((s) => (
                    <li key={s.id}>{s.intitule_session} — {s.statut_display}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {modale && (
        <div className="lab-modale-fond" onClick={fermerModale}>
          <div className="lab-modale" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>
                <i className="fa-solid fa-calendar-plus"></i>{" "}
                {modale === "creer" ? "Nouveau créneau" : "Modifier le créneau"}
              </h2>
              <button onClick={fermerModale}><i className="fa-solid fa-xmark"></i></button>
            </header>

            <form onSubmit={enregistrer}>
              <div className="champs-colonne">
                <label>Formation *
                  <select value={form.formation} onChange={(e) => majForm("formation", e.target.value)}>
                    <option value="">— Choisir —</option>
                    {formations.map((f) => (
                      <option key={f.id} value={f.id}>{f.intitule}</option>
                    ))}
                  </select>
                  {erreursForm.formation && <small className="err">{erreursForm.formation}</small>}
                </label>

                <label>Intitulé de la session *
                  <input
                    value={form.intitule_session}
                    onChange={(e) => majForm("intitule_session", e.target.value)}
                    placeholder="ex. React et son écosystème — Cohorte 003"
                  />
                  {erreursForm.intitule_session && <small className="err">{erreursForm.intitule_session}</small>}
                </label>

                <div className="champs-duo">
                  <label>Date de début
                    <input type="date" value={form.date_debut}
                           onChange={(e) => majForm("date_debut", e.target.value)} />
                  </label>
                  <label>Date de fin
                    <input type="date" value={form.date_fin} min={form.date_debut || undefined}
                           onChange={(e) => majForm("date_fin", e.target.value)} />
                  </label>
                </div>

                <SeancesEditor
                  key={ouverture}
                  dateDebut={form.date_debut}
                  dateFin={form.date_fin}
                  seances={form.seances}
                  onChange={changerSeances}
                  erreur={erreursForm.seances}
                />

                <ConflitSalle
                  messages={conflitSalle}
                  enCours={enregistrement}
                  onChanger={() => { setConflitSalle(null); document.getElementById("planning-salle")?.focus(); }}
                  onMaintenir={() => enregistrer(null, true)}
                />

                <div className="champs-duo">
                  <label>Mode
                    <select value={form.mode} onChange={(e) => changerMode(e.target.value)}>
                      {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </label>
                  <label>Salle
                    <select id="planning-salle" value={form.salle} disabled={form.mode === "distanciel"}
                            onChange={(e) => majForm("salle", e.target.value)}>
                      <option value="">{form.mode === "distanciel" ? "— Distanciel —" : "— Aucune —"}</option>
                      {salles.map((s) => {
                        const id = String(s.id);
                        const occupee = sallesLibres && !sallesLibres.has(id) && id !== String(form.salle);
                        return <option key={s.id} value={s.id}>{s.nom}{occupee ? " — occupée" : ""}</option>;
                      })}
                    </select>
                    {erreursForm.salle && <small className="err">{erreursForm.salle}</small>}
                    {form.mode !== "distanciel" && !erreursForm.salle && (
                      verifSalles
                        ? <small className="aide"><i className="fa-solid fa-spinner fa-spin"></i> Vérification des salles…</small>
                        : sallesLibres && <small className="aide">{sallesLibres.size} salle(s) libre(s) sur ces séances.</small>
                    )}
                  </label>
                </div>

                <div className="champs-duo">
                  <label>Niveau
                    <select value={form.niveau} onChange={(e) => majForm("niveau", e.target.value)}>
                      {NIVEAUX.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
                    </select>
                  </label>
                  <label>Statut
                    <select value={form.statut} onChange={(e) => majForm("statut", e.target.value)}>
                      {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </label>
                </div>

                <label>Formateur(s)
                  {formateurs.length ? (
                    <div className="planning-formateurs-liste">
                      {formateurs.map((f) => {
                        const id = String(f.id);
                        const nom = f.nom_complet || `${f.prenom} ${f.nom}`;
                        return (
                          <label key={f.id} className="planning-formateur-case">
                            <input type="checkbox" checked={form.formateurs.includes(id)}
                                   onChange={() => toggleFormateur(f.id)} />
                            {nom}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <small className="aide">Aucun formateur disponible.</small>
                  )}
                </label>
              </div>

              {banniereErreur && <div className="lab-flash erreur">{banniereErreur}</div>}

              <footer>
                <button type="button" className="btn-lien" onClick={fermerModale}>Annuler</button>
                <button type="submit" className="btn-primaire" disabled={enregistrement}>
                  {enregistrement ? "Enregistrement…" : (modale === "creer" ? "Créer le créneau" : "Enregistrer")}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
